import React from 'react';
import ArrowUpRightIcon from '../CustomIcons/ArrowUpRightIcon';
import PlusIcon from '../CustomIcons/PlusIcon';
import PillButton from '../PillButton';
import Tag from '../Tag';
import Typography from '../Typography';

export interface ChatContextItem {
    id: string;
    label: string;
    /** Shows a remove control on the item. Without it, the item reports rather than can be dropped. */
    onRemove?: () => void;
    /**
     * Opens what the item names, in a new tab. A reader deciding whether context is the right
     * context has to be able to look at it, and following the link in place would cost them the
     * conversation it belongs to.
     */
    href?: string;
}

export interface ChatContextProps {
    label?: string;
    items: ChatContextItem[];
    /** Renders the "Add context" control. Without it, the row only lists what is already in scope. */
    onAdd?: () => void;
    className?: string;
}

/**
 * What a conversation can see: the row under a chat header naming the policy, account or other
 * context a turn was given, plus an affordance to add more.
 *
 * Each item is a static `PillButton` (the same chip shape `Chat.ComposerChip` and
 * `Chat.ModelPicker` already use) rather than a bespoke chip — the remove control is its own
 * `Button`, the way a message's copy/rating actions are, so a chip is never the accessible name
 * for "remove this".
 */
const ChatContext: React.FC<ChatContextProps> = ({
    label = 'Context',
    items,
    onAdd,
    className = '',
}) => (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {/* Smaller than the header it sits under: this row names what the conversation can
            see, which is context for the title rather than a heading of its own.
            `Text`, not `SecondaryText`: the latter keeps a local scale where `xsmall` is
            12px, where the shared scale this needs puts it at 11. */}
        <Typography.Text size="xsmall" color="secondary">
            {label}
        </Typography.Text>
        {/* `Tag`, not an inert `PillButton`: these name what the conversation can see, and a
            de-activated button is a button pretending to be a label. Removal is `Tag`'s own
            trailing control rather than a `Button` beside it — outside the chip it reads as a
            second object floating next to the label, and an outline button draws a bordered box
            around a glyph that is already a circle. */}
        {items.map((item) => (
            <Tag
                key={item.id}
                value={item.label}
                type="default"
                fontSize="xsmall"
                paddingSize="compact"
                textTransform="none"
                iconPosition="right"
                {...(item.href
                    ? {
                          icon: (
                              <a
                                  href={item.href}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={`Open ${item.label} in a new tab`}
                                  className="inline-flex items-center opacity-70 hover:opacity-100 text-current"
                                  onClick={(event) => event.stopPropagation()}
                              >
                                  <ArrowUpRightIcon size={9} />
                              </a>
                          ),
                      }
                    : {})}
                {...(item.onRemove ? { onRemove: item.onRemove } : {})}
            />
        ))}
        {onAdd ? (
            <PillButton
                label="Add context"
                size="xsmall"
                icon={<PlusIcon size={11} />}
                onClick={onAdd}
            />
        ) : null}
    </div>
);

export default ChatContext;
export { ChatContext };
